import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole, ApiResponse, LoginInput, RegisterClientInput, RegisterAgentInput } from '@ouagaliv/shared';
import { api } from '../lib/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginInput) => Promise<void>;
  registerClient: (data: RegisterClientInput) => Promise<void>;
  registerAgent: (data: RegisterAgentInput) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          await refreshUser();
        } catch {
          logout();
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const refreshUser = async () => {
    const res = await api.get<ApiResponse<{ user: User }>>('/auth/profile');
    if (res.data.success && res.data.data) {
      setUser(res.data.data.user);
    }
  };

  const setAuth = (userData: User, tokens: { accessToken: string; refreshToken: string }) => {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    setUser(userData);
  };

  const login = async (data: LoginInput) => {
    const res = await api.post<ApiResponse<{ user: User; tokens: { accessToken: string; refreshToken: string } }>>('/auth/login', data);
    if (!res.data.success) throw new Error(res.data.error?.message || 'Login failed');
    setAuth(res.data.data!.user, res.data.data!.tokens);
  };

  const registerClient = async (data: RegisterClientInput) => {
    const res = await api.post<ApiResponse<{ user: User; tokens: { accessToken: string; refreshToken: string } }>>('/auth/register/client', data);
    if (!res.data.success) throw new Error(res.data.error?.message || 'Registration failed');
    setAuth(res.data.data!.user, res.data.data!.tokens);
  };

  const registerAgent = async (data: RegisterAgentInput) => {
    const res = await api.post<ApiResponse<{ user: User; tokens: { accessToken: string; refreshToken: string } }>>('/auth/register/agent', data);
    if (!res.data.success) throw new Error(res.data.error?.message || 'Registration failed');
    setAuth(res.data.data!.user, res.data.data!.tokens);
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  const updateProfile = async (data: Partial<User>) => {
    const res = await api.patch<ApiResponse<{ user: User }>>('/user/profile', data);
    if (!res.data.success) throw new Error(res.data.error?.message || 'Failed to update profile');
    if (res.data.data) {
      setUser(res.data.data.user);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, login, registerClient, registerAgent, logout, refreshUser, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}